import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { ThemeContext } from 'styled-components'

import { applyTextStyle } from '../../styles/typography'
import { fastTransition } from '../../styles/effects'
import { Icon } from '../icon'

interface IProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  inputId?: string
  listboxId?: string
  inputRef?: React.RefObject<HTMLInputElement>
  onClear?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

const FilterSearchHeader: React.FC<IProps> = ({
  value,
  onChange,
  placeholder,
  inputId,
  listboxId,
  inputRef,
  onClear,
  onKeyDown,
}) => {
  const theme = useContext(ThemeContext)
  const { t } = useTranslation()
  return (
    <Wrapper>
      <IconSlot>
        <Icon
          type="Search"
          size={14}
          color={value ? theme.colors.primary : theme.colors.text72}
        />
      </IconSlot>
      <Input
        type="text"
        role="searchbox"
        id={inputId}
        aria-controls={listboxId}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        ref={inputRef}
        onKeyDown={onKeyDown}
      />
      {value && (
        <ClearButton
          type="button"
          onClick={() => {
            onChange('')
            onClear?.()
          }}
          aria-label={t('general.forms.clearSearch')}
        >
          <Icon type="Close" size={12} color={theme.colors.text72} />
        </ClearButton>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 12px 16px;
`

const IconSlot = styled.span`
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  pointer-events: none;
`

const Input = styled.input`
  flex: 1;
  width: 100%;
  padding: 10px 40px 10px 36px;
  border: 1px solid ${({ theme }) => theme.colors.textLighter};
  border-radius: 8px;
  outline: 0;
  background-color: ${({ theme }) => theme.colors.lightest};
  color: ${({ theme }) => theme.colors.text};
  ${applyTextStyle('bodySmall')}
  transition: border-color ${fastTransition};

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text72};
  }
`

const ClearButton = styled.button`
  position: absolute;
  right: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
`

export default FilterSearchHeader
